import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import EyeOffFilledIcon from "@redotech/redo-icons/eye-off_filled.svg";
import ThumbsUpIcon from "@redotech/redo-icons/thumbs-up.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { Flex } from "@redotech/redo-web/flex";
import { ImageLightbox } from "@redotech/redo-web/image-lightbox";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

interface ChatMessageBubbleProps {
  message: Message;
  actions?: React.ReactNode;
  commentState?: "hidden" | "deleted";
  liked?: boolean;
}

export const ChatMessageBubble = memo(function ChatMessageBubble({
  message,
  actions,
  commentState,
  liked,
}: ChatMessageBubbleProps) {
  if (message.origin === "external") {
    return (
      <CustomerBubble
        actions={actions}
        commentState={commentState}
        liked={liked}
        message={message}
      />
    );
  }
  return (
    <MerchantBubble
      actions={actions}
      commentState={commentState}
      liked={liked}
      message={message}
    />
  );
});

const CustomerBubble = memo(function CustomerBubble({
  message,
  actions,
  commentState,
  liked,
}: ChatMessageBubbleProps) {
  const hasAttachments = message.attachments.length > 0;
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);

  return (
    <>
      <Flex className="group" dir="column" gap="xs" pr="5xl">
        <Text fontSize="xs" textColor="tertiary">
          {formatMessageTime(message.sentAt)}
        </Text>
        {commentState && (
          <Flex align="center" gap="xs" w="fit-content">
            {commentState === "hidden" ? (
              <>
                <EyeOffFilledIcon className="h-4 w-4 text-gray-500" />
                <Text fontSize="xs" textColor="tertiary">
                  This comment is hidden
                </Text>
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4 text-gray-500" />
                <Text fontSize="xs" textColor="tertiary">
                  This comment was deleted
                </Text>
              </>
            )}
          </Flex>
        )}
        {liked && (
          <Flex align="center" gap="xs" w="fit-content">
            <ThumbsUpIcon className="h-4 w-4 text-blue-500" />
            <Text fontSize="xs" textColor="tertiary">
              Liked
            </Text>
          </Flex>
        )}
        <Flex align="center" gap="sm" w="fit-content">
          <Flex dir="column" gap="sm" w="fit-content">
            {message.content && (
              <Flex bgColor="tertiary" p="lg" radius="md">
                <Text
                  fontSize="sm"
                  style={{ wordBreak: "break-word" }}
                  textColor={commentState ? "tertiary" : "primary"}
                >
                  {message.content}
                </Text>
              </Flex>
            )}
            {hasAttachments && (
              <Flex dir="column" gap="xs">
                {message.attachments.map((attachment) => {
                  const description =
                    attachment.description._tag === "Some"
                      ? attachment.description.value
                      : "Attachment";
                  const mimeType =
                    attachment.mimeType._tag === "Some"
                      ? attachment.mimeType.value
                      : null;
                  const isImage = mimeType?.startsWith("image/");

                  return (
                    <Flex dir="column" gap="xs" key={attachment.id}>
                      {isImage ? (
                        <img
                          alt={description}
                          onClick={() => setLightboxImageUrl(attachment.url)}
                          src={attachment.url}
                          style={{
                            maxWidth: "300px",
                            maxHeight: "300px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                        />
                      ) : (
                        <Flex
                          align="center"
                          bgColor="tertiary"
                          gap="sm"
                          p="md"
                          radius="md"
                        >
                          <Text fontSize="sm" textColor="primary">
                            {description}
                          </Text>
                          <a
                            href={attachment.url}
                            rel="noopener noreferrer"
                            style={{ color: "inherit" }}
                            target="_blank"
                          >
                            <Text fontSize="xs" textColor="secondary">
                              Download
                            </Text>
                          </a>
                        </Flex>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </Flex>
          {actions}
        </Flex>
      </Flex>
      <ImageLightbox
        imageSrc={lightboxImageUrl ?? undefined}
        onClose={() => setLightboxImageUrl(null)}
        open={lightboxImageUrl !== null}
      />
    </>
  );
});

const MerchantBubble = memo(function MerchantBubble({
  message,
  actions,
  commentState,
  liked,
}: ChatMessageBubbleProps) {
  const hasAttachments = message.attachments.length > 0;
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);

  return (
    <>
      <Flex align="flex-end" className="group" dir="column" gap="xs" pl="5xl">
        <Text fontSize="xs" textColor="tertiary">
          {formatMessageTime(message.sentAt)}
        </Text>
        {commentState && (
          <Flex align="center" gap="xs" w="fit-content">
            {commentState === "hidden" ? (
              <>
                <EyeOffFilledIcon className="h-4 w-4 text-gray-500" />
                <Text fontSize="xs" textColor="tertiary">
                  This comment is hidden
                </Text>
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4 text-gray-500" />
                <Text fontSize="xs" textColor="tertiary">
                  This comment was deleted
                </Text>
              </>
            )}
          </Flex>
        )}
        {liked && (
          <Flex align="center" gap="xs" w="fit-content">
            <ThumbsUpIcon className="h-4 w-4 text-blue-500" />
            <Text fontSize="xs" textColor="tertiary">
              Liked
            </Text>
          </Flex>
        )}
        <Flex align="center" gap="sm">
          <Flex dir="column" gap="sm">
            {message.content && (
              <Flex bgColor="alternate" p="lg" radius="md">
                <Text
                  fontSize="sm"
                  style={{ wordBreak: "break-word" }}
                  textColor={commentState ? "tertiary" : "white"}
                >
                  {message.content}
                </Text>
              </Flex>
            )}
            {hasAttachments && (
              <Flex dir="column" gap="xs">
                {message.attachments.map((attachment) => {
                  const description =
                    attachment.description._tag === "Some"
                      ? attachment.description.value
                      : "Attachment";
                  const mimeType =
                    attachment.mimeType._tag === "Some"
                      ? attachment.mimeType.value
                      : null;
                  const isImage = mimeType?.startsWith("image/");

                  return (
                    <Flex dir="column" gap="xs" key={attachment.id}>
                      {isImage ? (
                        <img
                          alt={description}
                          onClick={() => setLightboxImageUrl(attachment.url)}
                          src={attachment.url}
                          style={{
                            maxWidth: "300px",
                            maxHeight: "300px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                        />
                      ) : (
                        <Flex
                          align="center"
                          bgColor="alternate"
                          gap="sm"
                          p="md"
                          radius="md"
                        >
                          <Text fontSize="sm" textColor="white">
                            {description}
                          </Text>
                          <a
                            href={attachment.url}
                            rel="noopener noreferrer"
                            style={{ color: "inherit" }}
                            target="_blank"
                          >
                            <Text fontSize="xs" textColor="secondary">
                              Download
                            </Text>
                          </a>
                        </Flex>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </Flex>
          {actions}
        </Flex>
      </Flex>
      <ImageLightbox
        imageSrc={lightboxImageUrl ?? undefined}
        onClose={() => setLightboxImageUrl(null)}
        open={lightboxImageUrl !== null}
      />
    </>
  );
});

function formatMessageTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}
